// Scripts/FieldGuidance.js
// =========================
// Real-time Field Guidance Tool
// Calculates direct distance and bearing from User Location to a Target Pin.

import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Style, Stroke } from 'ol/style';
import Feature from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import { getDistance } from 'ol/sphere';
import { toLonLat } from 'ol/proj';
import { Icon } from 'ol/style';

let map = null;
let guidanceLayer = null;
let guidanceSource = null;
let guidanceFeature = null;
let targetPinFeature = null;
let targetCoordinate = null;
let isActive = false;
let guidanceLoopId = null;

const svgSurveyTarget = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#c41e3a" stroke="white" stroke-width="1.5"/>
        <path d="M12 8V16M8 12H16" stroke="white" stroke-width="2" stroke-linecap="round"/>
    </svg>`;
const surveyTargetUri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgSurveyTarget);

function init() {
    try { map = $('#map').data('map'); } catch (e) { }
    if (!map) return;

    guidanceSource = new VectorSource();
    guidanceLayer = new VectorLayer({
        source: guidanceSource,
        zIndex: 10000,
        style: (feature) => {
            const isFacing = feature.get('isFacingTarget');
            return new Style({
                stroke: new Stroke({
                    color: isFacing ? '#22c55e' : '#4285F4',
                    width: isFacing ? 4 : 3,
                    lineDash: isFacing ? null : [10, 10]
                })
            });
        }
    });
    map.addLayer(guidanceLayer);
}

function updateGuidance() {
    if (!isActive || !targetCoordinate || !window.GeoPST_Geolocation) return;

    // ✅ التعديل الجديد: إذا تم إيقاف الـ GPS، قم بإغلاق أداة التوجيه تلقائياً
    if (!window.GeoPST_Geolocation.isActive()) {
        window.stopFieldGuidance();
        return;
    }

    // 🔄 Smooth Animation Loop
    guidanceLoopId = requestAnimationFrame(updateGuidance);

    const currentPos = window.GeoPST_Geolocation.getPosition();
    if (!currentPos) {
        updateUI(null, null, false, null);
        if (guidanceFeature) {
            guidanceSource.removeFeature(guidanceFeature);
            guidanceFeature = null;
        }
        return;
    }

    // Update Line
    const coords = [currentPos, targetCoordinate];
    if (!guidanceFeature) {
        guidanceFeature = new Feature(new LineString(coords));
        guidanceSource.addFeature(guidanceFeature);
    } else {
        guidanceFeature.getGeometry().setCoordinates(coords);
    }

    // Calculate Metrics
    const proj = map.getView().getProjection();
    const lonLat1 = toLonLat(currentPos, proj);
    const lonLat2 = toLonLat(targetCoordinate, proj);

    const distance = getDistance(lonLat1, lonLat2);
    const bearing = calculateBearing(lonLat1[1], lonLat1[0], lonLat2[1], lonLat2[0]);

    // 🎯 Stakeout Heading Match (New!)
    let isFacingTarget = false;
    let deviceHeadingDeg = null;
    const deviceHeadingRad = window.GeoPST_Geolocation.getHeading(); // from Geolocation.js

    if (deviceHeadingRad !== null && deviceHeadingRad !== undefined) {
        deviceHeadingDeg = deviceHeadingRad * (180 / Math.PI);
        let diff = Math.abs(bearing - deviceHeadingDeg);
        if (diff > 180) diff = 360 - diff; // Wrap-around logic

        if (diff <= 3) {
            isFacingTarget = true;
        }
    }

    // Update Styling if state changed
    if (guidanceFeature.get('isFacingTarget') !== isFacingTarget) {
        guidanceFeature.set('isFacingTarget', isFacingTarget);
    }

    updateUI(distance, bearing, isFacingTarget, deviceHeadingDeg);
}

function calculateBearing(lat1, lon1, lat2, lon2) {
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) -
        Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x);
    return (θ * 180 / Math.PI + 360) % 360; // range [0, 360]
}

function updateUI(dist, bearing, isFacingTarget = false, currentHeading = null) {
    let panel = document.getElementById('field-guidance-panel');
    if (!panel) {
        panel = document.createElement('div');
        panel.id = 'field-guidance-panel';
        panel.className = 'field-guidance-overlay';
        panel.innerHTML = `
            <div class="guidance-metric">
                <span class="label">المسافة</span>
                <span class="value" id="guidance-dist-val" style="direction: ltr;">-</span>
            </div>
            <div class="guidance-divider"></div>
            <div class="guidance-metric">
                <span class="label">الاتجاه</span>
                <div class="heading-display" id="guidance-heading-container">
                    <span class="live-heading" id="guidance-live-heading">-</span>
                    <span class="separator">/</span>
                    <span class="target-bearing" id="guidance-target-bearing">-</span>
                </div>
            </div>
            <button class="stop-guidance-btn" onclick="window.stopFieldGuidance()">إغلاق</button>
        `;
        document.body.appendChild(panel);
    }

    const distEl = panel.querySelector('#guidance-dist-val');
    const liveHeadingEl = panel.querySelector('#guidance-live-heading');
    const targetBearingEl = panel.querySelector('#guidance-target-bearing');
    const headingContainer = panel.querySelector('#guidance-heading-container');

    if (dist === null) {
        distEl.innerHTML = '<small style="font-size:0.7rem">جاري البحث...</small>';
        liveHeadingEl.textContent = '-';
        targetBearingEl.textContent = '-';
        headingContainer.classList.remove('matching');
        return;
    }

    const distStr = dist > 1000 ? (dist / 1000).toFixed(2) + ' km' : dist.toFixed(0) + ' m';
    const bearingStr = bearing.toFixed(1) + '°';
    const headingStr = currentHeading !== null ? currentHeading.toFixed(1) + '°' : '...';

    distEl.textContent = distStr;
    liveHeadingEl.textContent = headingStr;
    targetBearingEl.textContent = bearingStr;

    if (isFacingTarget) {
        headingContainer.classList.add('matching');
    } else {
        headingContainer.classList.remove('matching');
    }
}

window.startFieldGuidance = (coord) => {
    if (!guidanceLayer) init();
    if (!map) return;

    targetCoordinate = coord;
    isActive = true;
    guidanceLayer.setVisible(true);

    // [ADD] Add persistent target pin feature
    if (targetPinFeature) guidanceSource.removeFeature(targetPinFeature);
    targetPinFeature = new Feature({
        geometry: new Point(coord)
    });
    targetPinFeature.setStyle(new Style({
        image: new Icon({
            anchor: [0.5, 0.5], // Centered for crosshair
            src: surveyTargetUri,
            scale: 1,
            imgSize: [24, 24]
        })
    }));
    guidanceSource.addFeature(targetPinFeature);

    if (window.GeoPST_Geolocation) {
        // Start tracking if not already active
        if (!window.GeoPST_Geolocation.isActive()) {
            window.GeoPST_Geolocation.start();
        }

        // Use animation frame for smooth heading updates
        if (guidanceLoopId) cancelAnimationFrame(guidanceLoopId);
        updateGuidance();
    }
};

window.stopFieldGuidance = () => {
    isActive = false;
    if (guidanceLayer) guidanceLayer.setVisible(false);
    if (guidanceFeature) {
        guidanceSource.removeFeature(guidanceFeature);
        guidanceFeature = null;
    }
    if (targetPinFeature) {
        guidanceSource.removeFeature(targetPinFeature);
        targetPinFeature = null;
    }
    const panel = document.getElementById('field-guidance-panel');
    if (panel) panel.remove();

    if (window.GeoPST_Geolocation) {
        // Stop the loop
        cancelAnimationFrame(guidanceLoopId);
    }
};

// CSS for the overlay
const style = document.createElement('style');
style.textContent = `
    .field-guidance-overlay {
        position: fixed;
        bottom: 10%;
        left: 50%;
        transform: translateX(-50%);
        background: var(--bg-primary);
        border: 1px solid var(--border-strong);
        border-radius: 20px;
        box-shadow: var(--shadow-modal);
        padding: 10px 20px;
        display: flex;
        align-items: center;
        gap: 16px;
        z-index: 10005;
        direction: rtl;
        font-family: inherit;
        transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        animation: guidanceSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    }

    /* Shift guidance panel up when Bottom Sheet is open to avoid collision is now handled centrally in components.css */
    .guidance-metric {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 80px;
    }
    .guidance-metric .label {
        font-size: 0.75rem;
        color: var(--text-secondary, #64748b);
        margin-bottom: 2px;
        font-weight: 500;
        white-space: nowrap;
    }
    .guidance-metric .value {
        font-size: 1.25rem;
        font-weight: 800;
        color: var(--text-primary);
        letter-spacing: -0.5px;
    }
    .heading-display {
        font-size: 1.25rem;
        font-weight: 800;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: 4px;
        direction: ltr;
        transition: color 0.3s ease;
        white-space: nowrap;
    }
    .heading-display.matching {
        color: #22c55e;
        text-shadow: 0 0 10px rgba(34, 197, 94, 0.2);
    }
    .heading-display .separator {
        opacity: 0.4;
        font-weight: 400;
        margin: 0 2px;
    }
    .heading-display .target-bearing {
        opacity: 0.7;
        font-size: 1.1rem;
    }
    .guidance-divider {
        width: 1.5px;
        height: 35px;
        background: var(--border-strong);
        opacity: 0.5;
    }
    .stop-guidance-btn {
        background: var(--primary);
        color: white;
        border: none;
        padding: 8px 18px;
        border-radius: 12px;
        font-size: 0.9rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s;
        margin-right: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    .stop-guidance-btn:hover {
        opacity: 0.9;
        transform: translateY(-1px);
    }
    .target-icon {
        margin-right: 4px;
        font-size: 1.1rem;
    }
    @keyframes guidanceSlideUp {
        from { transform: translate(-50%, 60px); opacity: 0; }
        to { transform: translate(-50%, 0); opacity: 1; }
    }
`;
document.head.appendChild(style);
